// Copyright (c) 2021 WiseTime. All rights reserved.

import { DateTime, SystemZone } from "luxon"
import { getCurrentDate, getTimelineDateParts, TimelineDate } from "../features/timeline/dateTime"

export type TimeZone = {
  // Compared to the Timeline's timeZone, this one always has a value
  // For example, when the userContext.timeZone is "Autodetect", the frontend
  // will automatically determine the local timezone from the browser.
  name: string,
  // Offset from UTC in hours.
  offset: number,
}

// Generates the most appropriate offset for the given timezone name.
// If "autodetect" or an invalid timezone is provided, the method returns
// the local timezone information provided by the browser
export const generateTimeZoneInfo = (timeZone: string | "AUTODETECT", date: TimelineDate = getCurrentDate()): TimeZone => {
  const {
    year,
    month,
    day,
  } = getTimelineDateParts(date)
  // Attempt to convert the given timeZone into a valid luxon timeZone
  const localDateTime = DateTime.local(year, month, day)
  const timeZoneDate = localDateTime.setZone(timeZone)
  if (timeZone.toUpperCase() === "AUTODETECT" || !timeZoneDate.isValid) {
    const localZoneDate = localDateTime.setZone(new SystemZone())
    return ({
      name: localZoneDate.zoneName,
      offset: localZoneDate.offset / 60,
    })
  }
  return ({
    name: timeZoneDate.zoneName,
    offset: timeZoneDate.offset / 60,
  })
}

export const toUtcTimeZone = (timeZoneOffset: number): string => {
  if (timeZoneOffset === 0) {
    return "UTC"
  }
  if (timeZoneOffset > 0) {
    return "UTC+" + timeZoneOffset
  }
  return "UTC" + timeZoneOffset
}

export const hasTimezoneChanged = (
  tzOffsetPrevRecorded: number,
  tzOffsetCurr: number
): boolean => {
  if (tzOffsetPrevRecorded === undefined) {
    return false
  }
  return tzOffsetPrevRecorded !== tzOffsetCurr
}
